/**
 * JobFilters — single Filter Hub: search, icon filters, dismissible active chips.
 */
import {
  ArrowUpDown,
  LayoutGrid,
  LayoutList,
  Radio,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getCreditCost } from '../../../../config/creditCosts';
import type { Job } from '../JobCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MatchFitId = 'all' | 'strong' | 'good';
export type RecRemoteId = 'any' | 'remote';
export type RecFulltimeId = 'any' | 'fulltime';
export type RecContractId = 'any' | 'contract';

export interface RecToolbarFilters {
  matchFit:   MatchFitId;
  recRemote:  RecRemoteId;
  recFulltime: RecFulltimeId;
  recContract: RecContractId;
}

export const defaultRecToolbarFilters: RecToolbarFilters = {
  matchFit: 'all',
  recRemote: 'any',
  recFulltime: 'any',
  recContract: 'any',
};

export const MATCH_FIT_OPTIONS: { value: MatchFitId; label: string }[] = [
  { value: 'all',    label: 'All matches' },
  { value: 'strong', label: '75%+ Strong fit' },
  { value: 'good',   label: '55%+ Good fit' },
];

export const REC_REMOTE_OPTIONS: { value: RecRemoteId; label: string }[] = [
  { value: 'any',    label: 'Any location' },
  { value: 'remote', label: 'Remote' },
];

export const REC_FULLTIME_OPTIONS: { value: RecFulltimeId; label: string }[] = [
  { value: 'any',      label: 'Any schedule' },
  { value: 'fulltime', label: 'Full-time' },
];

export const REC_CONTRACT_OPTIONS: { value: RecContractId; label: string }[] = [
  { value: 'any',      label: 'Any contract' },
  { value: 'contract', label: 'Contract' },
];

export function hasActiveRecToolbarFilters(f: RecToolbarFilters): boolean {
  return f.matchFit !== 'all'
    || f.recRemote !== 'any'
    || f.recFulltime !== 'any'
    || f.recContract !== 'any';
}

export function applyRecToolbarFilters(jobs: Job[], f: RecToolbarFilters): Job[] {
  let pool = jobs;
  if (f.matchFit === 'strong') pool = pool.filter(j => (j.matchScore ?? 0) >= 75);
  else if (f.matchFit === 'good') pool = pool.filter(j => (j.matchScore ?? 0) >= 55);
  if (f.recRemote === 'remote') {
    pool = pool.filter(j => j.remote === true || (j.location ?? '').toLowerCase().includes('remote'));
  }
  if (f.recFulltime === 'fulltime') {
    pool = pool.filter(j => (j.job_type ?? '').toLowerCase().includes('full'));
  }
  if (f.recContract === 'contract') {
    pool = pool.filter(j => (j.job_type ?? '').toLowerCase().includes('contract'));
  }
  return pool;
}

export type FreshnessId = 'any' | '24h' | '3d' | '7d' | '14d';
export type RemoteId    = 'any' | 'remote' | 'onsite';
export type SortById    = 'relevance' | 'date';

export interface JobFiltersState {
  search:     string;
  freshness:  FreshnessId;
  jobType:    string;
  experience: string;
  remote:     RemoteId;
  sortBy:     SortById;
}

export const defaultFilters: JobFiltersState = {
  search: '', freshness: 'any', jobType: 'any',
  experience: 'any', remote: 'any', sortBy: 'date',
};

export const FRESHNESS_DAYS: Record<FreshnessId, number | null> = {
  any: null, '24h': 1, '3d': 3, '7d': 7, '14d': 14,
};

const FRESHNESS_LABEL: Record<FreshnessId, string> = {
  any: 'Any time', '24h': 'Last 24 h', '3d': 'Last 3 days',
  '7d': 'Last 7 days', '14d': 'Last 14 days',
};

const REMOTE_LABEL: Record<RemoteId, string> = {
  any: 'Any location', remote: 'Remote', onsite: 'On-site',
};

// ---------------------------------------------------------------------------
// Filter application helper
// ---------------------------------------------------------------------------

export function applyJobFilters(jobs: Job[], f: JobFiltersState): Job[] {
  const q    = f.search.trim().toLowerCase();
  const days = FRESHNESS_DAYS[f.freshness];
  const now  = Date.now();

  let result = jobs.filter(job => {
    if (q) {
      const hay =
        job.title.toLowerCase() + ' ' +
        job.company.toLowerCase() + ' ' +
        (job.location ?? '').toLowerCase() + ' ' +
        (job.skills ?? []).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (days !== null) {
      const iso = job.posted_at ?? job.created;
      const t   = new Date(iso).getTime();
      if (Number.isFinite(t) && (now - t) / (1000 * 60 * 60 * 24) > days) return false;
    }
    if (f.jobType   !== 'any' && (job.job_type        ?? '').toLowerCase() !== f.jobType.toLowerCase())   return false;
    if (f.experience !== 'any' && (job.experience_level ?? '').toLowerCase() !== f.experience.toLowerCase()) return false;
    if (f.remote === 'remote' && !job.remote)  return false;
    if (f.remote === 'onsite' &&  job.remote)  return false;
    return true;
  });

  if (f.sortBy === 'date') {
    result = [...result].sort((a, b) =>
      new Date(b.posted_at ?? b.created).getTime() - new Date(a.posted_at ?? a.created).getTime()
    );
  } else if (f.sortBy === 'relevance') {
    result = [...result].sort((a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0));
  }

  return result;
}

// ---------------------------------------------------------------------------
// Toolbar dropdown
// ---------------------------------------------------------------------------

interface FilterSelectProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  ariaLabel: string;
  active: boolean;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  value, options, onChange, ariaLabel, active,
}) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    aria-label={ariaLabel}
    className="ds-select ds-select-sm ds-jobs-toolbar__filter shrink-0"
    style={{ color: active ? 'var(--ds-brand)' : 'var(--ds-text-body)' }}
  >
    {options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

interface ActiveChipProps {
  label: string;
  onDismiss: () => void;
  removeAriaLabel: string;
}

const ActiveFilterChip: React.FC<ActiveChipProps> = ({ label, onDismiss, removeAriaLabel }) => (
  <button
    type="button"
    onClick={onDismiss}
    className="ds-jobs-filter-hub__active-chip"
    aria-label={removeAriaLabel}
  >
    <span className="truncate max-w-[10rem]">{label}</span>
    <X className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
  </button>
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

const STANDARD_EXP_LEVELS = [
  'Fresher', 'Entry Level', 'Junior', 'Mid', 'Senior', 'Lead',
];

const BUCKET_LABEL = ['Fresher', 'Entry Level', 'Junior', 'Mid', 'Senior', 'Lead'];

interface JobFiltersProps {
  jobs:              Job[];
  value:             JobFiltersState;
  onChange:          (next: JobFiltersState) => void;
  totalCount:        number;
  filteredCount:     number;
  onFetchLive?:      () => void;
  isFetchingLive?:   boolean;
  viewMode?:         'grid' | 'table';
  onViewModeChange?: (mode: 'grid' | 'table') => void;
  experienceYears?:  number | null;
  /** AI match-fit filters (grid mode) */
  showRecFilters?:      boolean;
  recToolbar?:          RecToolbarFilters;
  onRecToolbarChange?:  (next: RecToolbarFilters) => void;
  recFilterLoading?:    boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const JobFilters: React.FC<JobFiltersProps> = ({
  jobs, value, onChange,
  totalCount, filteredCount,
  onFetchLive, isFetchingLive,
  viewMode = 'grid', onViewModeChange,
  experienceYears,
  showRecFilters = false,
  recToolbar = defaultRecToolbarFilters,
  onRecToolbarChange,
  recFilterLoading = false,
}) => {
  const { t, i18n } = useTranslation();

  const matchFitOptions = useMemo(() => [
    { value: 'all' as MatchFitId, label: t('dashboard.jobs.filters.matchAll', 'All matches') },
    { value: 'strong' as MatchFitId, label: t('dashboard.jobs.filters.matchStrong', '75%+ Strong fit') },
    { value: 'good' as MatchFitId, label: t('dashboard.jobs.filters.matchGood', '55%+ Good fit') },
  ], [t, i18n.language]);

  const recRemoteOptions = useMemo(() => [
    { value: 'any' as RecRemoteId, label: t('dashboard.jobs.filters.anyLocation', 'Any location') },
    { value: 'remote' as RecRemoteId, label: t('dashboard.jobs.jobCard.remote', 'Remote') },
  ], [t, i18n.language]);

  const recFulltimeOptions = useMemo(() => [
    { value: 'any' as RecFulltimeId, label: t('dashboard.jobs.filters.anySchedule', 'Any schedule') },
    { value: 'fulltime' as RecFulltimeId, label: t('dashboard.jobs.jobPreferences.jobType.fulltime', 'Full-time') },
  ], [t, i18n.language]);

  const recContractOptions = useMemo(() => [
    { value: 'any' as RecContractId, label: t('dashboard.jobs.filters.anyContract', 'Any contract') },
    { value: 'contract' as RecContractId, label: t('dashboard.jobs.jobPreferences.jobType.contract', 'Contract') },
  ], [t, i18n.language]);

  const freshnessLabel = useMemo((): Record<FreshnessId, string> => ({
    any: t('dashboard.jobs.filters.freshnessAny', 'Any time'),
    '24h': t('dashboard.jobs.filters.freshness24h', 'Last 24 h'),
    '3d': t('dashboard.jobs.filters.freshness3d', 'Last 3 days'),
    '7d': t('dashboard.jobs.filters.freshness7d', 'Last 7 days'),
    '14d': t('dashboard.jobs.filters.freshness14d', 'Last 14 days'),
  }), [t, i18n.language]);

  const remoteLabel = useMemo((): Record<RemoteId, string> => ({
    any: t('dashboard.jobs.filters.anyLocation', 'Any location'),
    remote: t('dashboard.jobs.jobCard.remote', 'Remote'),
    onsite: t('dashboard.jobs.filters.onsite', 'On-site'),
  }), [t, i18n.language]);

  const jobTypeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs) if (j.job_type) set.add(j.job_type);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [jobs]);

  const experienceOptions = useMemo(() => {
    const set = new Set<string>(STANDARD_EXP_LEVELS);
    for (const j of jobs) if (j.experience_level) set.add(j.experience_level);
    return Array.from(set);
  }, [jobs]);

  void experienceYears;
  void BUCKET_LABEL;

  const patch = (p: Partial<JobFiltersState>) => onChange({ ...value, ...p });

  const clearAll = () => onChange(defaultFilters);
  const searchRef = useRef<HTMLInputElement>(null);

  const freshnessOptions = useMemo(
    () => (Object.keys(freshnessLabel) as FreshnessId[]).map(k => ({
      value: k,
      label: freshnessLabel[k],
    })),
    [freshnessLabel],
  );

  const jobTypeSelectOptions = useMemo(
    () => [
      { value: 'any', label: t('dashboard.jobs.filters.anyType', 'Any type') },
      ...jobTypeOptions.map(j => ({ value: j, label: j })),
    ],
    [jobTypeOptions, t, i18n.language],
  );

  const experienceSelectOptions = useMemo(
    () => [
      { value: 'any', label: t('dashboard.jobs.filters.anyExperience', 'Any experience') },
      ...experienceOptions.map(x => ({ value: x, label: x })),
    ],
    [experienceOptions, t, i18n.language],
  );

  const remoteOptions = useMemo(
    () => (Object.keys(remoteLabel) as RemoteId[]).map(k => ({
      value: k,
      label: remoteLabel[k],
    })),
    [remoteLabel],
  );

  const activeChips = useMemo(() => {
    const chips: { id: string; label: string; dismiss: () => void }[] = [];
    if (value.search.trim()) {
      chips.push({
        id: 'search',
        label: `"${value.search.trim()}"`,
        dismiss: () => onChange({ ...value, search: '' }),
      });
    }
    if (value.freshness !== 'any') {
      chips.push({
        id: 'freshness',
        label: freshnessLabel[value.freshness],
        dismiss: () => onChange({ ...value, freshness: 'any' }),
      });
    }
    if (value.jobType !== 'any') {
      chips.push({
        id: 'jobType',
        label: value.jobType,
        dismiss: () => onChange({ ...value, jobType: 'any' }),
      });
    }
    if (value.experience !== 'any') {
      chips.push({
        id: 'experience',
        label: value.experience,
        dismiss: () => onChange({ ...value, experience: 'any' }),
      });
    }
    if (value.remote !== 'any') {
      chips.push({
        id: 'remote',
        label: remoteLabel[value.remote],
        dismiss: () => onChange({ ...value, remote: 'any' }),
      });
    }
    return chips;
  }, [value, freshnessLabel, remoteLabel, onChange]);

  const fetchLiveTitle = t('dashboard.jobs.filters.fetchLiveTitle', {
    defaultValue: 'FETCH Live — pull fresh jobs from Peerlist, LinkedIn, Naukri & more ({{credits}} credits)',
    credits: getCreditCost('job_feed_refresh'),
  });

  const hasStructuredFilters =
    value.freshness !== 'any' ||
    value.jobType !== 'any' ||
    value.experience !== 'any' ||
    value.remote !== 'any';

  return (
    <div key={i18n.language} className="flex-shrink-0 space-y-2">

      {/* ── Filter Hub (search + active chips) ── */}
      <div className="ds-jobs-filter-hub">
        <div className="relative px-3 pt-3 pb-2">
          <Search className="pointer-events-none absolute left-5 top-[calc(50%-0.25rem)] h-4 w-4 -translate-y-1/2 t-muted" />
          <input
            ref={searchRef}
            type="text"
            value={value.search}
            onChange={e => patch({ search: e.target.value })}
            placeholder={t('dashboard.jobs.filters.searchPlaceholder', 'Search jobs…')}
            className="ds-input w-full pl-9 pr-9"
          />
          {value.search && (
            <button
              type="button"
              onClick={() => { patch({ search: '' }); searchRef.current?.focus(); }}
              aria-label={t('dashboard.jobs.filters.clearSearch', 'Clear search')}
              className="ds-btn ds-btn-ghost ds-btn-sm absolute right-2 top-1/2 -translate-y-1/2"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {(activeChips.length > 0 || value.search.trim()) && (
          <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
            {activeChips.map(chip => (
              <ActiveFilterChip
                key={chip.id}
                label={chip.label}
                onDismiss={chip.dismiss}
                removeAriaLabel={t('dashboard.jobs.filters.removeFilter', {
                  defaultValue: 'Remove filter: {{label}}',
                  label: chip.label,
                })}
              />
            ))}
            {(hasStructuredFilters || value.search.trim()) && (
              <button type="button" onClick={clearAll} className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 t-meta">
                {t('dashboard.jobs.filters.clearAll', 'Clear all')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Results toolbar: count + filters + sort + view ── */}
      <div className="ds-jobs-toolbar space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex shrink-0 items-center gap-1.5">
            <SlidersHorizontal className="h-3.5 w-3.5 t-muted" />
            <span className="t-meta t-num">
              <strong className="t-strong">{filteredCount !== totalCount ? filteredCount : totalCount}</strong>
              {filteredCount !== totalCount && (
                <span>{t('dashboard.jobs.filters.countOf', { defaultValue: ' of {{total}}', total: totalCount })}</span>
              )}
              <span> {t('dashboard.jobs.filters.jobsLabel', 'jobs')}</span>
            </span>
          </div>

          <div className="flex-1 min-w-[0.5rem] hidden sm:block" />

          <div className="flex shrink-0 flex-wrap items-center gap-1.5 ml-auto sm:ml-0">
            <div className="relative flex shrink-0 items-center">
              <ArrowUpDown className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 t-muted" aria-hidden />
              <select
                value={value.sortBy}
                onChange={e => patch({ sortBy: e.target.value as SortById })}
                aria-label={t('dashboard.jobs.filters.sortBy', 'Sort by')}
                className="ds-select ds-select-sm ds-jobs-toolbar__sort shrink-0 !pl-7"
                style={{ color: value.sortBy !== 'date' ? 'var(--ds-brand)' : 'var(--ds-text-body)' }}
              >
                <option value="date">{t('dashboard.jobs.filters.sortDate', 'Date posted')}</option>
                <option value="relevance">{t('dashboard.jobs.filters.sortRelevance', 'Relevance')}</option>
              </select>
            </div>

            {onViewModeChange && (
              <div
                className="hidden sm:flex shrink-0 items-center overflow-hidden rounded-lg border"
                style={{ borderColor: 'var(--ds-border-strong)' }}
              >
                <button
                  type="button"
                  onClick={() => onViewModeChange('grid')}
                  aria-label={t('dashboard.jobs.filters.gridView', 'Grid view')}
                  title={t('dashboard.jobs.filters.gridView', 'Grid view')}
                  className={['ds-btn ds-btn-sm rounded-none border-0 min-h-[44px] min-w-[44px]', viewMode === 'grid' ? 'ds-btn-secondary' : 'ds-btn-ghost'].join(' ')}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </button>
                <div className="ds-divider m-0 h-4 w-px" />
                <button
                  type="button"
                  onClick={() => onViewModeChange('table')}
                  aria-label={t('dashboard.jobs.filters.tableView', 'Table view')}
                  title={t('dashboard.jobs.filters.tableView', 'Table view')}
                  className={['ds-btn ds-btn-sm rounded-none border-0 min-h-[44px] min-w-[44px]', viewMode === 'table' ? 'ds-btn-secondary' : 'ds-btn-ghost'].join(' ')}
                >
                  <LayoutList className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {onFetchLive && (
              <button
                type="button"
                onClick={onFetchLive}
                disabled={isFetchingLive}
                title={fetchLiveTitle}
                aria-label={t('dashboard.jobs.filters.fetchLive', 'Fetch live jobs')}
                className="ds-btn ds-btn-primary ds-btn-sm shrink-0 gap-1.5 min-h-[44px] touch-manipulation"
              >
                <Radio className={['h-3.5 w-3.5', isFetchingLive ? 'animate-pulse' : ''].join(' ')} />
                {t('dashboard.jobs.filters.fetchLiveButton', 'FETCH Live')}
              </button>
            )}
          </div>
        </div>

        <div className="ds-jobs-toolbar__filters-scroll -mx-1 px-1">
          <div className="ds-jobs-toolbar__filters-inner flex items-center gap-2 pb-0.5">
            {showRecFilters && recFilterLoading && (
              <span className="t-meta t-muted shrink-0 whitespace-nowrap">{t('dashboard.jobs.filters.matching', 'Matching…')}</span>
            )}
            {showRecFilters && onRecToolbarChange && (
              <>
                <FilterSelect
                  value={recToolbar.matchFit}
                  options={matchFitOptions}
                  onChange={v => onRecToolbarChange({ ...recToolbar, matchFit: v as MatchFitId })}
                  ariaLabel={t('dashboard.jobs.filters.matchFit', 'Match fit')}
                  active={recToolbar.matchFit !== 'all'}
                />
                <FilterSelect
                  value={recToolbar.recRemote}
                  options={recRemoteOptions}
                  onChange={v => onRecToolbarChange({ ...recToolbar, recRemote: v as RecRemoteId })}
                  ariaLabel={t('dashboard.jobs.jobCard.remote', 'Remote')}
                  active={recToolbar.recRemote !== 'any'}
                />
                <FilterSelect
                  value={recToolbar.recFulltime}
                  options={recFulltimeOptions}
                  onChange={v => onRecToolbarChange({ ...recToolbar, recFulltime: v as RecFulltimeId })}
                  ariaLabel={t('dashboard.jobs.jobPreferences.jobType.fulltime', 'Full-time')}
                  active={recToolbar.recFulltime !== 'any'}
                />
                <FilterSelect
                  value={recToolbar.recContract}
                  options={recContractOptions}
                  onChange={v => onRecToolbarChange({ ...recToolbar, recContract: v as RecContractId })}
                  ariaLabel={t('dashboard.jobs.jobPreferences.jobType.contract', 'Contract')}
                  active={recToolbar.recContract !== 'any'}
                />
              </>
            )}
            <FilterSelect
              value={value.freshness}
              options={freshnessOptions}
              onChange={v => patch({ freshness: v as FreshnessId })}
              ariaLabel={t('dashboard.jobs.filters.postedTime', 'Posted time')}
              active={value.freshness !== 'any'}
            />
            <FilterSelect
              value={value.jobType}
              options={jobTypeSelectOptions}
              onChange={v => patch({ jobType: v })}
              ariaLabel={t('dashboard.jobs.jobPreferences.jobTypeLabel', 'Job type')}
              active={value.jobType !== 'any'}
            />
            <FilterSelect
              value={value.experience}
              options={experienceSelectOptions}
              onChange={v => patch({ experience: v })}
              ariaLabel={t('dashboard.jobs.jobPreferences.experienceLabel', 'Experience level')}
              active={value.experience !== 'any'}
            />
            <FilterSelect
              value={value.remote}
              options={remoteOptions}
              onChange={v => patch({ remote: v as RemoteId })}
              ariaLabel={t('dashboard.jobs.jobPreferences.location', 'Location')}
              active={value.remote !== 'any'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(JobFilters);
